import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Ship, Users, Calendar, CheckCircle, ChevronRight, ChevronLeft,
  Search, Filter, Target, AlertTriangle, Clock, MapPin, Award,
  Briefcase, Globe, ArrowRight, Check, X, Sparkles, FileCheck,
  UserPlus, UsersRound, ToggleLeft, ToggleRight
} from 'lucide-react';
import { crewApi } from '../services/api';
import type { Vessel, CrewMember, ReliefPlan, WorkflowStats, VesselCrewPlan } from '../types';
import { RANKS, VESSEL_CATEGORIES } from '../types';
import VesselCrewPlanning from '../components/VesselCrewPlanning';

type WorkflowMode = 'single' | 'crew_planning';
type WorkflowStep = 'vessel' | 'position' | 'candidate' | 'review' | 'confirm' | 'crew_planning';

interface AssignmentData {
  vessel: Vessel | null;
  rank: string;
  vesselCategory: string;
  selectedCrew: CrewMember | null;
  notes: string;
  signOnDate: string;
  duration: number;
}

// Progress Steps Component
const ProgressSteps = ({ currentStep, steps }: { currentStep: WorkflowStep; steps: { key: WorkflowStep; label: string; icon: React.ReactNode }[] }) => {
  const currentIndex = steps.findIndex(s => s.key === currentStep);

  return (
    <div className="flex items-center justify-between mb-8">
      {steps.map((step, index) => (
        <div key={step.key} className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              index < currentIndex ? 'bg-emerald-500 text-white' :
              index === currentIndex ? 'bg-blue-600 text-white ring-4 ring-blue-100' :
              'bg-gray-100 text-gray-400'
            }`}>
              {index < currentIndex ? <Check className="w-6 h-6" /> : step.icon}
            </div>
            <span className={`mt-2 text-sm font-medium ${
              index <= currentIndex ? 'text-gray-900' : 'text-gray-400'
            }`}>
              {step.label}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div className={`w-full h-1 mx-4 rounded-full ${
              index < currentIndex ? 'bg-emerald-500' : 'bg-gray-200'
            }`} style={{ minWidth: '80px' }} />
          )}
        </div>
      ))}
    </div>
  );
};

// Vessel Selection Step
const VesselSelectionStep = ({
  vessels,
  selectedVessel,
  onSelect,
  isLoading
}: {
  vessels: Vessel[];
  selectedVessel: Vessel | null;
  onSelect: (vessel: Vessel) => void;
  isLoading: boolean;
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const filteredVessels = vessels.filter(v => {
    const matchesSearch = v.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || v.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Select Vessel</h2>
        <p className="text-gray-600 mt-1">Choose the vessel that needs a crew assignment</p>
      </div>

      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search vessels..."
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">All Categories</option>
          {VESSEL_CATEGORIES.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 max-h-[400px] overflow-y-auto pr-2">
          {filteredVessels.map((vessel) => (
            <motion.div
              key={vessel.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onSelect(vessel)}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selectedVessel?.id === vessel.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-blue-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-lg ${
                  selectedVessel?.id === vessel.id ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <Ship className={`w-6 h-6 ${
                    selectedVessel?.id === vessel.id ? 'text-blue-600' : 'text-gray-500'
                  }`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{vessel.name}</h3>
                  <p className="text-sm text-gray-500">{vessel.category || 'Unknown'} • {vessel.teu_capacity?.toLocaleString() || 'N/A'} TEU</p>
                </div>
                {selectedVessel?.id === vessel.id && (
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Position Selection Step
const PositionSelectionStep = ({
  selectedRank,
  onSelect
}: {
  selectedRank: string;
  onSelect: (rank: string) => void;
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Select Position</h2>
        <p className="text-gray-600 mt-1">Choose the rank/position to fill</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {RANKS.map((rank) => (
          <motion.div
            key={rank.code}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onSelect(rank.code)}
            className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
              selectedRank === rank.code
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-blue-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">{rank.label}</h3>
                <p className="text-sm text-gray-500">{rank.department} Department</p>
              </div>
              {selectedRank === rank.code && (
                <CheckCircle className="w-6 h-6 text-blue-600" />
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// Candidate Selection Step
const CandidateSelectionStep = ({
  candidates,
  selectedCrew,
  onSelect,
  onFindMatches,
  isLoading
}: {
  candidates: CrewMember[];
  selectedCrew: CrewMember | null;
  onSelect: (crew: CrewMember) => void;
  onFindMatches: () => void;
  isLoading: boolean;
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCandidates = candidates.filter(c => {
    const name = `${c.name} ${c.surname}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase()) || c.cid.includes(searchQuery);
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Select Crew Member</h2>
          <p className="text-gray-600 mt-1">Choose from AI-matched candidates</p>
        </div>
        <button
          onClick={onFindMatches}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 font-medium rounded-lg hover:bg-purple-200 transition-colors"
        >
          <Sparkles className="w-4 h-4" />
          AI Match
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search candidates..."
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      ) : candidates.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="font-semibold text-gray-900">No candidates found</h3>
          <p className="text-gray-500 mt-1">Click "AI Match" to find suitable crew members</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[350px] overflow-y-auto pr-2">
          {filteredCandidates.map((crew, index) => (
            <motion.div
              key={crew.cid}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => onSelect(crew)}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selectedCrew?.cid === crew.cid
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-blue-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      selectedCrew?.cid === crew.cid ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <span className="text-lg font-bold text-gray-600">
                        {crew.name?.charAt(0)}{crew.surname?.charAt(0)}
                      </span>
                    </div>
                    {crew.match_score && (
                      <div className={`absolute -top-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                        crew.match_score >= 90 ? 'bg-emerald-500 text-white' :
                        crew.match_score >= 70 ? 'bg-amber-500 text-white' :
                        'bg-gray-400 text-white'
                      }`}>
                        {crew.match_score}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{crew.name} {crew.surname}</h3>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        {crew.nationality}
                      </span>
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        {crew.experience_months || 0} months
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">
                    {crew.vessel_type || crew.crew_type || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-400">
                    Available: {crew.due_date || crew.due_soff_date || 'Now'}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Review Step
const ReviewStep = ({
  data,
  onEdit
}: {
  data: AssignmentData;
  onEdit: (step: WorkflowStep) => void;
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Review Assignment</h2>
        <p className="text-gray-600 mt-1">Verify all details before confirming</p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Vessel Card */}
        <div className="p-6 bg-gray-50 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Vessel</h3>
            <button onClick={() => onEdit('vessel')} className="text-sm text-blue-600 hover:text-blue-700">Edit</button>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Ship className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">{data.vessel?.name}</div>
              <div className="text-sm text-gray-500">{data.vessel?.category} • {data.vessel?.teu_capacity?.toLocaleString()} TEU</div>
            </div>
          </div>
        </div>

        {/* Position Card */}
        <div className="p-6 bg-gray-50 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Position</h3>
            <button onClick={() => onEdit('position')} className="text-sm text-blue-600 hover:text-blue-700">Edit</button>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-100 rounded-lg">
              <Briefcase className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <div className="font-medium text-gray-900">
                {RANKS.find(r => r.code === data.rank)?.label || data.rank}
              </div>
              <div className="text-sm text-gray-500">
                {RANKS.find(r => r.code === data.rank)?.department} Department
              </div>
            </div>
          </div>
        </div>

        {/* Crew Member Card */}
        <div className="p-6 bg-gray-50 rounded-xl col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Assigned Crew Member</h3>
            <button onClick={() => onEdit('candidate')} className="text-sm text-blue-600 hover:text-blue-700">Edit</button>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center">
                <span className="text-xl font-bold text-purple-600">
                  {data.selectedCrew?.name?.charAt(0)}{data.selectedCrew?.surname?.charAt(0)}
                </span>
              </div>
              {data.selectedCrew?.match_score && (
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                  {data.selectedCrew.match_score}
                </div>
              )}
            </div>
            <div>
              <div className="font-medium text-gray-900">
                {data.selectedCrew?.name} {data.selectedCrew?.surname}
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                <span>ID: {data.selectedCrew?.cid}</span>
                <span>{data.selectedCrew?.nationality}</span>
                <span>{data.selectedCrew?.experience_months || 0} months exp.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Assignment Details */}
        <div className="p-6 bg-gray-50 rounded-xl col-span-2">
          <h3 className="font-semibold text-gray-900 mb-4">Assignment Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-500 mb-1">Sign-on Date</label>
              <div className="flex items-center gap-2 text-gray-900">
                <Calendar className="w-4 h-4 text-gray-400" />
                {data.signOnDate || 'To be determined'}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Expected Duration</label>
              <div className="flex items-center gap-2 text-gray-900">
                <Clock className="w-4 h-4 text-gray-400" />
                {data.duration} months
              </div>
            </div>
          </div>
          {data.notes && (
            <div className="mt-4">
              <label className="block text-sm text-gray-500 mb-1">Notes</label>
              <p className="text-gray-900">{data.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Workflow Mode Selector Component
const WorkflowModeSelector = ({
  mode,
  onModeChange
}: {
  mode: WorkflowMode;
  onModeChange: (mode: WorkflowMode) => void;
}) => {
  return (
    <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Assignment Mode</h3>
      <div className="grid grid-cols-2 gap-4">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onModeChange('single')}
          className={`p-4 rounded-xl border-2 transition-all text-left ${
            mode === 'single'
              ? 'border-blue-500 bg-white shadow-md'
              : 'border-gray-200 bg-white/50 hover:border-blue-300'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${mode === 'single' ? 'bg-blue-100' : 'bg-gray-100'}`}>
              <UserPlus className={`w-5 h-5 ${mode === 'single' ? 'text-blue-600' : 'text-gray-500'}`} />
            </div>
            <span className="font-medium text-gray-900">Single Assignment</span>
            {mode === 'single' && <CheckCircle className="w-5 h-5 text-blue-600 ml-auto" />}
          </div>
          <p className="text-sm text-gray-500 ml-11">
            Assign one crew member to a specific position step by step
          </p>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onModeChange('crew_planning')}
          className={`p-4 rounded-xl border-2 transition-all text-left ${
            mode === 'crew_planning'
              ? 'border-indigo-500 bg-white shadow-md'
              : 'border-gray-200 bg-white/50 hover:border-indigo-300'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${mode === 'crew_planning' ? 'bg-indigo-100' : 'bg-gray-100'}`}>
              <UsersRound className={`w-5 h-5 ${mode === 'crew_planning' ? 'text-indigo-600' : 'text-gray-500'}`} />
            </div>
            <span className="font-medium text-gray-900">Full Crew Planning</span>
            {mode === 'crew_planning' && <CheckCircle className="w-5 h-5 text-indigo-600 ml-auto" />}
          </div>
          <p className="text-sm text-gray-500 ml-11">
            AI suggests complete crew complement with accept/reject options
          </p>
        </motion.button>
      </div>
    </div>
  );
};

// Rank Selection for Crew Planning
const CrewPlanningRankSelector = ({
  selectedRanks,
  onRanksChange
}: {
  selectedRanks: string[];
  onRanksChange: (ranks: string[]) => void;
}) => {
  const toggleRank = (rank: string) => {
    if (selectedRanks.includes(rank)) {
      onRanksChange(selectedRanks.filter(r => r !== rank));
    } else {
      onRanksChange([...selectedRanks, rank]);
    }
  };

  const selectAll = () => {
    onRanksChange(RANKS.map(r => r.code));
  };

  const clearAll = () => {
    onRanksChange([]);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-gray-900">Select Positions to Fill</h3>
        <div className="flex gap-2">
          <button
            onClick={selectAll}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            Select All
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={clearAll}
            className="text-sm text-gray-600 hover:text-gray-700"
          >
            Clear
          </button>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {RANKS.map((rank) => (
          <motion.button
            key={rank.code}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => toggleRank(rank.code)}
            className={`p-3 rounded-lg border-2 transition-all ${
              selectedRanks.includes(rank.code)
                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-300'
            }`}
          >
            <div className="text-center">
              <div className="font-bold">{rank.code}</div>
              <div className="text-xs mt-1 opacity-70">{rank.name}</div>
            </div>
          </motion.button>
        ))}
      </div>
      <p className="text-sm text-gray-500">
        {selectedRanks.length} position{selectedRanks.length !== 1 ? 's' : ''} selected
      </p>
    </div>
  );
};

// Main Workflow Page
export default function AssignmentWorkflowPage() {
  const navigate = useNavigate();
  const [workflowMode, setWorkflowMode] = useState<WorkflowMode>('single');
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('vessel');
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [candidates, setCandidates] = useState<CrewMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [workflowStats, setWorkflowStats] = useState<WorkflowStats | null>(null);
  const [selectedRanksForPlanning, setSelectedRanksForPlanning] = useState<string[]>([
    'MSTR', 'COFF', 'CENG', '3ENG', '4ENG'
  ]);
  const [confirmedCrewPlan, setConfirmedCrewPlan] = useState<VesselCrewPlan | null>(null);

  const [assignmentData, setAssignmentData] = useState<AssignmentData>({
    vessel: null,
    rank: '',
    vesselCategory: '',
    selectedCrew: null,
    notes: '',
    signOnDate: '',
    duration: 6,
  });

  const singleAssignmentSteps = [
    { key: 'vessel' as WorkflowStep, label: 'Select Vessel', icon: <Ship className="w-6 h-6" /> },
    { key: 'position' as WorkflowStep, label: 'Position', icon: <Briefcase className="w-6 h-6" /> },
    { key: 'candidate' as WorkflowStep, label: 'Candidate', icon: <Users className="w-6 h-6" /> },
    { key: 'review' as WorkflowStep, label: 'Review', icon: <FileCheck className="w-6 h-6" /> },
    { key: 'confirm' as WorkflowStep, label: 'Confirm', icon: <CheckCircle className="w-6 h-6" /> },
  ];

  const crewPlanningSteps = [
    { key: 'vessel' as WorkflowStep, label: 'Select Vessel', icon: <Ship className="w-6 h-6" /> },
    { key: 'crew_planning' as WorkflowStep, label: 'AI Planning', icon: <Sparkles className="w-6 h-6" /> },
    { key: 'confirm' as WorkflowStep, label: 'Confirm', icon: <CheckCircle className="w-6 h-6" /> },
  ];

  const steps = workflowMode === 'single' ? singleAssignmentSteps : crewPlanningSteps;

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const [vesselsRes, statsRes] = await Promise.all([
        crewApi.getVessels(),
        crewApi.getWorkflowStats(),
      ]);
      setVessels(vesselsRes.vessels);
      setWorkflowStats(statsRes);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const findMatchingCandidates = async () => {
    if (!assignmentData.vessel || !assignmentData.rank) return;
    
    setIsLoading(true);
    try {
      const response = await crewApi.matchCrewToVessel(
        assignmentData.vessel.id,
        assignmentData.rank,
        assignmentData.vessel.category
      );
      setCandidates(response.matches);
    } catch (error) {
      console.error('Failed to find matches:', error);
      // Fallback to available crew
      const available = await crewApi.getAvailableCrew({ rank: assignmentData.rank });
      setCandidates(available.crew.slice(0, 20));
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    const stepOrder: WorkflowStep[] = workflowMode === 'single' 
      ? ['vessel', 'position', 'candidate', 'review', 'confirm']
      : ['vessel', 'crew_planning', 'confirm'];
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      setCurrentStep(stepOrder[currentIndex + 1]);
    }
  };

  const handlePrevious = () => {
    const stepOrder: WorkflowStep[] = workflowMode === 'single' 
      ? ['vessel', 'position', 'candidate', 'review', 'confirm']
      : ['vessel', 'crew_planning', 'confirm'];
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1]);
    }
  };

  const handleCrewPlanConfirm = async (plan: VesselCrewPlan) => {
    setIsLoading(true);
    try {
      // Create assignments for all accepted/changed positions
      const acceptedPositions = plan.positions.filter(
        p => p.status === 'accepted' || p.status === 'changed'
      );
      
      for (const position of acceptedPositions) {
        const crew = position.selected_crew || position.suggested_crew;
        if (crew && assignmentData.vessel) {
          await crewApi.createAssignment({
            crew_cid: crew.cid,
            vessel_id: assignmentData.vessel.id,
            vessel_name: assignmentData.vessel.name,
            rank: position.position,
            expected_duration_months: assignmentData.duration,
          });
        }
      }
      
      setConfirmedCrewPlan(plan);
      setCurrentStep('confirm');
    } catch (error) {
      console.error('Failed to create assignments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!assignmentData.vessel || !assignmentData.selectedCrew) return;

    setIsLoading(true);
    try {
      await crewApi.createAssignment({
        crew_cid: assignmentData.selectedCrew.cid,
        vessel_id: assignmentData.vessel.id,
        vessel_name: assignmentData.vessel.name,
        rank: assignmentData.rank,
        sign_on_date: assignmentData.signOnDate || undefined,
        expected_duration_months: assignmentData.duration,
        notes: assignmentData.notes || undefined,
      });
      setCurrentStep('confirm');
    } catch (error) {
      console.error('Failed to create assignment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 'vessel': 
        if (workflowMode === 'crew_planning') {
          return !!assignmentData.vessel && selectedRanksForPlanning.length > 0;
        }
        return !!assignmentData.vessel;
      case 'position': return !!assignmentData.rank;
      case 'candidate': return !!assignmentData.selectedCrew;
      case 'review': return true;
      case 'crew_planning': return false; // Handled by the crew planning component
      default: return false;
    }
  };

  const handleModeChange = (mode: WorkflowMode) => {
    setWorkflowMode(mode);
    setCurrentStep('vessel');
    setAssignmentData({
      vessel: null,
      rank: '',
      vesselCategory: '',
      selectedCrew: null,
      notes: '',
      signOnDate: '',
      duration: 6,
    });
    setCandidates([]);
    setConfirmedCrewPlan(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Target className="w-8 h-8 mr-3 text-blue-600" />
            Crew Assignment Workflow
          </h1>
          <p className="text-gray-600 mt-2">
            {workflowMode === 'single' 
              ? 'Step-by-step wizard to assign qualified crew to vessel positions'
              : 'AI-powered crew planning with full vessel complement suggestions'
            }
          </p>
        </div>

        {/* Mode Selector - Only show at start */}
        {currentStep === 'vessel' && !assignmentData.vessel && (
          <WorkflowModeSelector 
            mode={workflowMode} 
            onModeChange={handleModeChange} 
          />
        )}

        {/* Workflow Stats */}
        {workflowStats && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="p-4 bg-white rounded-xl border border-gray-200">
              <div className="text-2xl font-bold text-amber-600">{workflowStats.total_pending}</div>
              <div className="text-sm text-gray-500">Pending</div>
            </div>
            <div className="p-4 bg-white rounded-xl border border-gray-200">
              <div className="text-2xl font-bold text-emerald-600">{workflowStats.total_confirmed}</div>
              <div className="text-sm text-gray-500">Confirmed</div>
            </div>
            <div className="p-4 bg-white rounded-xl border border-gray-200">
              <div className="text-2xl font-bold text-red-600">{workflowStats.total_unassigned}</div>
              <div className="text-sm text-gray-500">Unassigned</div>
            </div>
            <div className="p-4 bg-white rounded-xl border border-gray-200">
              <div className="text-2xl font-bold text-blue-600">{workflowStats.completion_rate}%</div>
              <div className="text-sm text-gray-500">Completion</div>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-8">
          <ProgressSteps currentStep={currentStep} steps={steps} />

          {/* Step Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {currentStep === 'vessel' && (
                <div className="space-y-8">
                  <VesselSelectionStep
                    vessels={vessels}
                    selectedVessel={assignmentData.vessel}
                    onSelect={(vessel) => {
                      setAssignmentData({ ...assignmentData, vessel, vesselCategory: vessel.category || '' });
                    }}
                    isLoading={isLoading}
                  />
                  
                  {/* Rank selector for crew planning mode */}
                  {workflowMode === 'crew_planning' && assignmentData.vessel && (
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <CrewPlanningRankSelector
                        selectedRanks={selectedRanksForPlanning}
                        onRanksChange={setSelectedRanksForPlanning}
                      />
                    </div>
                  )}
                </div>
              )}

              {currentStep === 'crew_planning' && assignmentData.vessel && (
                <VesselCrewPlanning
                  vessel={assignmentData.vessel}
                  vesselCategory={assignmentData.vesselCategory || 'Large'}
                  requiredRanks={selectedRanksForPlanning}
                  onConfirm={handleCrewPlanConfirm}
                  onCancel={() => setCurrentStep('vessel')}
                />
              )}

              {currentStep === 'position' && (
                <PositionSelectionStep
                  selectedRank={assignmentData.rank}
                  onSelect={(rank) => setAssignmentData({ ...assignmentData, rank })}
                />
              )}

              {currentStep === 'candidate' && (
                <CandidateSelectionStep
                  candidates={candidates}
                  selectedCrew={assignmentData.selectedCrew}
                  onSelect={(crew) => setAssignmentData({ ...assignmentData, selectedCrew: crew })}
                  onFindMatches={findMatchingCandidates}
                  isLoading={isLoading}
                />
              )}

              {currentStep === 'review' && (
                <ReviewStep
                  data={assignmentData}
                  onEdit={(step) => setCurrentStep(step)}
                />
              )}

              {currentStep === 'confirm' && (
                <div className="text-center py-12">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6"
                  >
                    <CheckCircle className="w-12 h-12 text-emerald-600" />
                  </motion.div>
                  
                  {workflowMode === 'crew_planning' && confirmedCrewPlan ? (
                    <>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">Crew Plan Confirmed!</h2>
                      <p className="text-gray-600 mb-4">
                        {confirmedCrewPlan.positions.filter(p => p.status === 'accepted' || p.status === 'changed').length} crew members have been assigned to {assignmentData.vessel?.name}
                      </p>
                      
                      {/* Show assigned crew list */}
                      <div className="max-w-md mx-auto mb-8">
                        <div className="bg-gray-50 rounded-xl p-4 text-left">
                          {confirmedCrewPlan.positions
                            .filter(p => p.status === 'accepted' || p.status === 'changed')
                            .map((pos) => {
                              const crew = pos.selected_crew || pos.suggested_crew;
                              return (
                                <div key={pos.position} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0">
                                  <span className="font-medium text-gray-900">
                                    {RANKS.find(r => r.code === pos.position)?.label || pos.position}
                                  </span>
                                  <span className="text-gray-600">
                                    {crew?.name}
                                  </span>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">Assignment Created!</h2>
                      <p className="text-gray-600 mb-8">
                        {assignmentData.selectedCrew?.name} {assignmentData.selectedCrew?.surname} has been assigned to {assignmentData.vessel?.name}
                      </p>
                    </>
                  )}
                  
                  <div className="flex justify-center gap-4">
                    <button
                      onClick={() => {
                        setAssignmentData({
                          vessel: null,
                          rank: '',
                          vesselCategory: '',
                          selectedCrew: null,
                          notes: '',
                          signOnDate: '',
                          duration: 6,
                        });
                        setCandidates([]);
                        setConfirmedCrewPlan(null);
                        setCurrentStep('vessel');
                      }}
                      className="px-6 py-3 bg-white border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      Create Another
                    </button>
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
                    >
                      Go to Dashboard
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation Buttons */}
        {currentStep !== 'confirm' && currentStep !== 'crew_planning' && (
          <div className="flex justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 'vessel'}
              className="flex items-center gap-2 px-6 py-3 text-gray-600 font-medium rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
              Previous
            </button>

            {currentStep === 'review' ? (
              <button
                onClick={handleSubmit}
                disabled={!canProceed() || isLoading}
                className="flex items-center gap-2 px-8 py-3 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Creating...' : 'Create Assignment'}
                <CheckCircle className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {workflowMode === 'crew_planning' && currentStep === 'vessel' 
                  ? 'Generate Crew Plan' 
                  : 'Next'
                }
                {workflowMode === 'crew_planning' && currentStep === 'vessel' 
                  ? <Sparkles className="w-5 h-5" />
                  : <ChevronRight className="w-5 h-5" />
                }
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

